import React, { useState } from 'react';
import { Alert, App, Button, Card, Input, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import employeeService, { employeeKeys, type ProfileChangeRequestRecord } from '@/services/employee.service';
import { useAuthStore } from '@/store/authStore';
import { hrmsStatusColor } from './shared/hrmsStatus';

const { Text } = Typography;

const ProfileChangeRequestsPage: React.FC = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const canReview =
    Boolean(user?.is_super_admin) || Boolean(user?.permissions?.includes('hr.employee.edit'));
  const [comment, setComment] = useState<Record<string, string>>({});

  const { data: rows = [], isLoading } = useQuery({
    queryKey: [...employeeKeys.all, 'company-pcr'],
    queryFn: () => employeeService.listCompanyProfileChangeRequests(),
  });

  const review = async (id: string, approve: boolean) => {
    try {
      await employeeService.reviewProfileChangeRequest(id, {
        approve,
        review_comment: comment[id],
      });
      message.success(approve ? 'Approved — employee profile updated' : 'Rejected');
      queryClient.invalidateQueries({ queryKey: [...employeeKeys.all, 'company-pcr'] });
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    } catch {
      message.error('Review failed');
    }
  };

  const columns: ColumnsType<ProfileChangeRequestRecord> = [
    {
      title: 'Employee',
      key: 'emp',
      render: (_, r) => (
        <Button type="link" size="small" onClick={() => navigate(`/hr/employees/${r.employee_id}`)}>
          Open profile
        </Button>
      ),
    },
    { title: 'Scope', dataIndex: 'field_key', width: 120 },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 140,
      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
    },
    {
      title: 'Requested changes',
      dataIndex: 'new_value_json',
      ellipsis: true,
      render: (json: string) => {
        try {
          const o = JSON.parse(json || '{}');
          return Object.keys(o).join(', ') || json;
        } catch {
          return json;
        }
      },
    },
    ...(canReview
      ? [
          {
            title: 'Comment',
            key: 'c',
            render: (_: unknown, r: ProfileChangeRequestRecord) => (
              <Input
                size="small"
                placeholder="Optional"
                value={comment[r.id] || ''}
                onChange={(e) => setComment((m) => ({ ...m, [r.id]: e.target.value }))}
              />
            ),
          },
          {
            title: '',
            key: 'a',
            width: 200,
            render: (_: unknown, r: ProfileChangeRequestRecord) =>
              r.status === 'Pending' || r.status === 'PendingApproval' ? (
                <Space>
                  <Button type="primary" size="small" onClick={() => review(r.id, true)}>
                    Approve
                  </Button>
                  <Button danger size="small" onClick={() => review(r.id, false)}>
                    Reject
                  </Button>
                </Space>
              ) : null,
          },
        ]
      : []),
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Profile change requests"
          subtitle="Approve versioned employee profile updates (compensation stays on Salary revisions)"
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Approval applies changes to the employee master"
        description="Approving increments the employee profile version. Rejecting leaves the master unchanged."
      />
      <Card>
        <Table rowKey="id" loading={isLoading} columns={columns} dataSource={rows} pagination={{ pageSize: 20 }} />
      </Card>
    </WorkspaceLayout>
  );
};

export default ProfileChangeRequestsPage;
